import { useMemo } from 'react';
import { FiDollarSign, FiCheckCircle, FiAlertCircle, FiArchive } from 'react-icons/fi';
import { StatCard, StatCardGrid } from '../../../components/common';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate }  from '../../../utils/dateUtils';
import styles from '../MemberReports.module.css';

const categoryName = (o) => o.DonationCategory?.name || o.donationCategoryId?.name || '—';
const memberName   = (o) => o.member?.name || o.userId?.name || '—';

const lastPaidDate = (o) => {
  const dates = (o.settlements || [])
    .map((s) => s.Receipt?.receiptDate)
    .filter(Boolean)
    .sort();
  return dates.length > 0 ? dates[dates.length - 1] : null;
};

const groupByCategory = (list) => {
  const map = {};
  list.forEach((o) => {
    const name = categoryName(o);
    if (!map[name]) map[name] = { name, count: 0, offered: 0, paid: 0, pending: 0 };
    map[name].count++;
    map[name].offered  += Number(o.offeredAmount)  || 0;
    map[name].paid     += Number(o.paidAmount)     || 0;
    map[name].pending  += Number(o.pendingAmount)  || 0;
  });
  return Object.values(map).sort((a, b) => a.name.localeCompare(b.name));
};

const subLabel = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: 8,
};

const catSection = {
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 6,
  overflowX: 'auto',
  marginBottom: 16,
};

export default function MemberLedgerReport({ offerings, currentFyId, selectedMemberId, isUserOnly = false }) {
  /* ── All offerings for selected member ────────────────────────────────── */
  const memberOfferings = useMemo(() =>
    selectedMemberId
      ? offerings.filter((o) => String(o.userId?.id ?? o.userId) === selectedMemberId)
      : [],
    [offerings, selectedMemberId]
  );

  /* ── Pending / partial — all financial years ──────────────────────────── */
  const pendingOfferings = useMemo(() =>
    memberOfferings
      .filter((o) => o.status === 'pending' || o.status === 'partial')
      .sort((a, b) => (a.offeringDate || '').localeCompare(b.offeringDate || '')),
    [memberOfferings]
  );

  /* ── Paid entries in the current financial year ───────────────────────── */
  const paidOfferings = useMemo(() =>
    memberOfferings
      .filter((o) => {
        const fyId = String(o.financialYearId?.id ?? o.financialYearId ?? '');
        return String(currentFyId) === fyId && Number(o.paidAmount) > 0;
      })
      .sort((a, b) => {
        const da = lastPaidDate(a) || a.offeringDate || '';
        const db = lastPaidDate(b) || b.offeringDate || '';
        return db.localeCompare(da);
      }),
    [memberOfferings, currentFyId]
  );

  /* ── Category breakdowns ──────────────────────────────────────────────── */
  const pendingByCategory = useMemo(() => groupByCategory(pendingOfferings), [pendingOfferings]);
  const paidByCategory    = useMemo(() => groupByCategory(paidOfferings),    [paidOfferings]);

  /* ── Stat totals ──────────────────────────────────────────────────────── */
  const totalPending = pendingOfferings.reduce((s, o) => s + (Number(o.pendingAmount) || 0), 0);
  const totalPaid    = paidOfferings.reduce((s, o) => s + (Number(o.paidAmount) || 0), 0);
  const hasData = memberOfferings.length > 0;

  /* ── Render ───────────────────────────────────────────────────────────── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {!selectedMemberId && !isUserOnly && (
        <div className="card">
          <div className="empty-state">Select a member from the filter bar above to view their ledger.</div>
        </div>
      )}

      {selectedMemberId && !hasData && (
        <div className="card">
          <div className="empty-state">No offering records found for this member.</div>
        </div>
      )}

      {selectedMemberId && hasData && (
        <>
          {/* Summary stat cards */}
          <StatCardGrid cols={4}>
            <StatCard
              icon={<FiArchive size={18} />}
              iconBg="rgba(245,158,11,0.12)"
              iconColor="#f59e0b"
              label="Pending Entries"
              value={String(pendingOfferings.length)}
            />
            <StatCard
              icon={<FiAlertCircle size={18} />}
              iconBg="rgba(239,68,68,0.12)"
              iconColor="#ef4444"
              label="Total Pending"
              value={fmtCurrency(totalPending)}
              valueColor="red"
            />
            <StatCard
              icon={<FiCheckCircle size={18} />}
              iconBg="rgba(16,185,129,0.12)"
              iconColor="#10b981"
              label="Paid Entries (This Year)"
              value={String(paidOfferings.length)}
            />
            <StatCard
              icon={<FiDollarSign size={18} />}
              iconBg="rgba(99,102,241,0.12)"
              iconColor="#6366f1"
              label="Total Paid (This Year)"
              value={fmtCurrency(totalPaid)}
              valueColor="green"
            />
          </StatCardGrid>

          {/* ── Pending & Partial section ──────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3>All Offering Entries: Pending &amp; Partial</h3>
              <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>Across all financial years</span>
            </div>

            {/* By category — plain bordered box, no table-container */}
            {pendingByCategory.length > 0 && (
              <>
                <p style={subLabel}>By Category</p>
                <div style={catSection}>
                  <table style={{ fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>Category</th>
                        <th style={{ width: 70, textAlign: 'center' }}>Entries</th>
                        <th className={styles.amtRight}>Offered (₹)</th>
                        <th className={styles.amtRight}>Paid (₹)</th>
                        <th className={styles.amtRight}>Pending (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingByCategory.map((c, i) => (
                        <tr key={c.name}>
                          <td>{i + 1}</td>
                          <td><strong>{c.name}</strong></td>
                          <td style={{ textAlign: 'center' }}>{c.count}</td>
                          <td className={styles.amtRight}>{fmtCurrency(c.offered)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(c.paid)}</td>
                          <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(c.pending)}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td style={{ textAlign: 'center' }}><strong>{pendingOfferings.length}</strong></td>
                        <td className={styles.amtRight}><strong>{fmtCurrency(pendingByCategory.reduce((s, c) => s + c.offered, 0))}</strong></td>
                        <td className={styles.amtRight}><strong>{fmtCurrency(pendingByCategory.reduce((s, c) => s + c.paid, 0))}</strong></td>
                        <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totalPending)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p style={subLabel}>All Entries</p>
              </>
            )}

            {/* All entries detail */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Category</th>
                    <th>Offering Date</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                    <th className={styles.amtRight}>Pending (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOfferings.length === 0 && (
                    <tr><td colSpan={7} className="empty-state">No pending or partial entries — all clear!</td></tr>
                  )}
                  {pendingOfferings.map((o, i) => (
                    <tr key={o.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{categoryName(o)}</td>
                      <td>{formatDate(o.offeringDate)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                      <td className={`${styles.amtRight} ${styles.pending}`}>
                        <strong>{fmtCurrency(o.pendingAmount)}</strong>
                      </td>
                      <td>
                        <span className={`badge badge-${o.status === 'partial' ? 'submitted' : 'pending'}`}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {pendingOfferings.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={3}><strong>Total ({pendingOfferings.length} entr{pendingOfferings.length !== 1 ? 'ies' : 'y'})</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingByCategory.reduce((s, c) => s + c.offered, 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(pendingByCategory.reduce((s, c) => s + c.paid, 0))}</strong></td>
                      <td className={`${styles.amtRight} ${styles.pending}`}><strong>{fmtCurrency(totalPending)}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* ── Paid section ──────────────────────────────────────────────── */}
          <div className="card">
            <div className="card-header">
              <h3>Paid</h3>
              <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>Current financial year</span>
            </div>

            {/* By category — plain bordered box, no table-container */}
            {paidByCategory.length > 0 && (
              <>
                <p style={subLabel}>By Category</p>
                <div style={catSection}>
                  <table style={{ fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>#</th>
                        <th>Category</th>
                        <th style={{ width: 70, textAlign: 'center' }}>Entries</th>
                        <th className={styles.amtRight}>Offered (₹)</th>
                        <th className={styles.amtRight}>Paid (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidByCategory.map((c, i) => (
                        <tr key={c.name}>
                          <td>{i + 1}</td>
                          <td><strong>{c.name}</strong></td>
                          <td style={{ textAlign: 'center' }}>{c.count}</td>
                          <td className={styles.amtRight}>{fmtCurrency(c.offered)}</td>
                          <td className={styles.amtRight}>{fmtCurrency(c.paid)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td style={{ textAlign: 'center' }}><strong>{paidOfferings.length}</strong></td>
                        <td className={styles.amtRight}><strong>{fmtCurrency(paidByCategory.reduce((s, c) => s + c.offered, 0))}</strong></td>
                        <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p style={subLabel}>All Entries</p>
              </>
            )}

            {/* All entries detail */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Category</th>
                    <th>Offering Date</th>
                    <th>Paid Date</th>
                    <th className={styles.amtRight}>Offered (₹)</th>
                    <th className={styles.amtRight}>Paid (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {paidOfferings.length === 0 && (
                    <tr><td colSpan={7} className="empty-state">No payments recorded in the current financial year.</td></tr>
                  )}
                  {paidOfferings.map((o, i) => (
                    <tr key={o.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{categoryName(o)}</td>
                      <td>{formatDate(o.offeringDate)}</td>
                      <td>{formatDate(lastPaidDate(o)) || '—'}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.offeredAmount)}</td>
                      <td className={styles.amtRight}>{fmtCurrency(o.paidAmount)}</td>
                      <td>
                        <span className={`badge badge-${
                          o.status === 'completed' ? 'approved' :
                          o.status === 'partial'   ? 'submitted' : 'pending'
                        }`}>
                          {o.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {paidOfferings.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan={4}><strong>Total ({paidOfferings.length} entr{paidOfferings.length !== 1 ? 'ies' : 'y'})</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(paidByCategory.reduce((s, c) => s + c.offered, 0))}</strong></td>
                      <td className={styles.amtRight}><strong>{fmtCurrency(totalPaid)}</strong></td>
                      <td />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
